"use client"

import Header from "@/components/header"
import Footer from "@/components/footer"
import { StarfieldBackground } from "@/components/starfield-background"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  FileSearch,
  CheckCircle2,
  ArrowRight,
  Bug,
  Code,
  Lock,
  FileCheck,
  Cpu,
  Wallet,
  Sparkles,
  Shield,
  Zap,
  Crown
} from "lucide-react"
import Link from "next/link"
import { motion } from "framer-motion"

const auditScope = [
  {
    icon: Bug,
    title: "Vulnerability Detection",
    description: "Identify reentrancy, overflow, front-running, and other common smart contract vulnerabilities"
  },
  {
    icon: Code,
    title: "Code Quality Review",
    description: "Assess code structure, readability, and adherence to best practices and standards"
  },
  {
    icon: Cpu,
    title: "Gas Optimization",
    description: "Analyze and recommend optimizations to reduce transaction costs for users"
  },
  {
    icon: Lock,
    title: "Access Control",
    description: "Verify proper implementation of permissions, roles, and admin functions"
  }
]

const auditProcess = [
  { step: 1, title: "Submit Contract", description: "Provide your smart contract code and documentation" },
  { step: 2, title: "Initial Review", description: "Our auditors perform automated and manual analysis" },
  { step: 3, title: "Detailed Report", description: "Receive comprehensive findings with severity ratings" },
  { step: 4, title: "Remediation", description: "Fix issues and get a final verification audit" }
]

const findings = [
  { severity: "Critical", color: "red", description: "Issues that could lead to loss of funds or contract takeover" },
  { severity: "High", color: "orange", description: "Significant vulnerabilities that could impact contract functionality" },
  { severity: "Medium", color: "yellow", description: "Issues that could affect contract behavior under certain conditions" },
  { severity: "Low", color: "blue", description: "Minor issues, code improvements, and best practice suggestions" }
]

const pricing = [
  {
    name: "Basic Audit",
    price: "FREE",
    description: "Limited Time Offer",
    icon: Shield,
    color: "from-green-500 to-emerald-600",
    features: [
      "Up to 300 lines of code",
      "Automated vulnerability scan",
      "Basic code review",
      "PDF audit report",
      "7-10 day turnaround"
    ],
    isFree: true,
    badge: "Limited Time"
  },
  {
    name: "Standard Audit",
    price: "$200",
    description: "For presale & token contracts",
    icon: Zap,
    color: "from-blue-500 to-cyan-600",
    features: [
      "Up to 1,000 lines of code",
      "Comprehensive analysis",
      "Manual code review",
      "Gas optimization tips",
      "Audit badge for project",
      "5-7 day turnaround",
      "1 re-audit included"
    ],
    popular: true
  },
  {
    name: "Premium Audit",
    price: "$600",
    description: "For complex DeFi protocols",
    icon: Crown,
    color: "from-purple-500 to-pink-600",
    features: [
      "Unlimited lines of code",
      "Full protocol analysis",
      "Economic model review",
      "Priority support",
      "48-hour express option",
      "Unlimited re-audits",
      "Featured listing"
    ]
  }
]

const stats = [
  { value: "100+", label: "Contracts Audited" },
  { value: "50+", label: "Critical Issues Found" },
  { value: "$10M+", label: "Funds Protected" },
  { value: "0", label: "Post-Audit Exploits" }
]

export function AuditContent() {
  return (
    <div className="min-h-screen flex flex-col relative">
      <StarfieldBackground />
      <Header />
      <main className="flex-1 relative z-10">
        {/* Hero Section */}
        <section className="py-20 md:py-28">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-sm font-medium text-blue-500">
                <FileSearch className="h-4 w-4" />
                Smart Contract Audit Service
              </div>
              <h1 className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
                Secure Your{" "}
                <span className="bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent">
                  Smart Contracts
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                Professional smart contract auditing to identify vulnerabilities,
                optimize gas usage, and build investor confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/audit/request">
                  <Button size="lg" className="bg-blue-500 hover:bg-blue-600">
                    Request Audit
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline">
                  View Sample Report
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 border-y border-border/40">
          <div className="container">
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="text-3xl md:text-4xl font-bold text-blue-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Audit Scope */}
        <section className="py-20">
          <div className="container">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">What We Audit</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Our comprehensive audit covers all aspects of your smart contract security
              </p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {auditScope.map((item, index) => {
                const Icon = item.icon
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="group border-border/40 hover:border-blue-500/50 transition-all h-full">
                      <CardContent className="p-6">
                        <div className="mb-4">
                          <motion.div
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            className="h-14 w-14 flex items-center justify-center rounded-2xl bg-blue-500/10 group-hover:bg-blue-500/20 transition-all"
                          >
                            <Icon className="h-7 w-7 text-blue-500" />
                          </motion.div>
                        </div>
                        <h3 className="font-bold mb-2">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 bg-muted/30">
          <div className="container">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Our Audit Process</h2>
              <p className="text-muted-foreground">Rigorous methodology for comprehensive security analysis</p>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {auditProcess.map((item, index) => (
                  <motion.div
                    key={item.step}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative"
                  >
                    <div className="text-center p-6 rounded-xl bg-background border border-border/40 hover:border-blue-500/50 transition-all">
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className="h-14 w-14 mx-auto flex items-center justify-center rounded-full bg-blue-500 text-white font-bold text-xl mb-4"
                      >
                        {item.step}
                      </motion.div>
                      <h3 className="font-bold mb-2">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Severity Levels */}
        <section className="py-20">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Finding Severity Levels</h2>
                <p className="text-muted-foreground">
                  All findings are categorized by severity to help you prioritize fixes
                </p>
              </div>
              <div className="space-y-4">
                {findings.map((finding, index) => (
                  <motion.div
                    key={finding.severity}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex items-center gap-4 p-4 rounded-xl border ${finding.severity === 'Critical' ? 'border-red-500/50 bg-red-500/5' :
                      finding.severity === 'High' ? 'border-orange-500/50 bg-orange-500/5' :
                        finding.severity === 'Medium' ? 'border-yellow-500/50 bg-yellow-500/5' :
                          'border-blue-500/50 bg-blue-500/5'
                      }`}
                  >
                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${finding.severity === 'Critical' ? 'bg-red-500 text-white' :
                      finding.severity === 'High' ? 'bg-orange-500 text-white' :
                        finding.severity === 'Medium' ? 'bg-yellow-500 text-black' :
                          'bg-blue-500 text-white'
                      }`}>
                      {finding.severity}
                    </div>
                    <p className="text-muted-foreground">{finding.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="py-20 bg-muted/30">
          <div className="container">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Audit Packages</h2>
              <p className="text-muted-foreground mb-4">Transparent pricing for every project size</p>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <Wallet className="h-4 w-4 text-primary" />
                <span className="text-sm">Pay with BNB, ETH, USDT, or POL</span>
              </div>
            </div>
            <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-3 max-w-5xl mx-auto">
              {pricing.map((plan, index) => {
                const Icon = plan.icon
                return (
                  <motion.div
                    key={plan.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card
                      className={`relative h-full ${plan.popular ? 'border-blue-500 shadow-lg shadow-blue-500/20' : 'border-border/40'}`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-blue-500 text-white">Most Popular</Badge>
                        </div>
                      )}
                      {plan.badge && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white flex items-center gap-1">
                            <Sparkles className="h-3 w-3" />
                            {plan.badge}
                          </Badge>
                        </div>
                      )}
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className="text-center mb-6">
                          <motion.div
                            whileHover={{ rotate: 5, scale: 1.1 }}
                            className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${plan.color} flex items-center justify-center`}
                          >
                            <Icon className="w-8 h-8 text-white" />
                          </motion.div>
                          <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                          <div className={`text-4xl font-bold mb-2 bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>
                            {plan.price}
                          </div>
                          <p className="text-sm text-muted-foreground">{plan.description}</p>
                        </div>
                        <ul className="space-y-3 mb-6 flex-1">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-blue-500 shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                        <Link href="/audit/request">
                          <Button
                            className={`w-full bg-gradient-to-r ${plan.color} hover:opacity-90`}
                          >
                            {plan.isFree ? 'Request Free Audit' : 'Get Started'}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Audit Badge */}
        <section className="py-20">
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h2 className="text-3xl font-bold mb-6">Earn the Audit Badge</h2>
                <p className="text-muted-foreground mb-6">
                  Projects that pass our audit receive a verified badge displayed on their
                  presale page, instantly communicating security to potential investors.
                </p>
                <ul className="space-y-4">
                  {[
                    "Displayed prominently on your presale page",
                    "Links to full audit report for transparency",
                    "Increases investor confidence significantly",
                    "Featured in our verified projects listing"
                  ].map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <CheckCircle2 className="h-5 w-5 text-blue-500" />
                      <span>{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="glass-card p-8"
              >
                <div className="flex items-center gap-4 mb-6">
                  <motion.div
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    className="h-16 w-16 rounded-full bg-blue-500/10 flex items-center justify-center"
                  >
                    <FileCheck className="h-8 w-8 text-blue-500" />
                  </motion.div>
                  <div>
                    <div className="text-2xl font-bold">Audit Passed</div>
                    <div className="text-muted-foreground">Security Verified</div>
                  </div>
                </div>
                <p className="text-muted-foreground mb-6">
                  This badge confirms that the smart contract has been professionally
                  audited and no critical or high-severity issues remain.
                </p>
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                  <FileCheck className="h-5 w-5 text-blue-500" />
                  <span className="font-semibold text-blue-500">Audit Passed</span>
                  <span className="text-xs text-muted-foreground ml-auto">View Report →</span>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-muted/30">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl font-bold mb-4">Secure Your Smart Contract</h2>
              <p className="text-muted-foreground mb-8">
                Don't launch without a professional audit. Protect your investors and your reputation.
              </p>
              <Link href="/audit/request">
                <Button size="lg" className="bg-blue-500 hover:bg-blue-600">
                  Request Audit Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
